import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { connect } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import mux from 'mux-embed'

import Player, {
  PLAYER_ERROR_CODES,
  PLAYER_REPORTABLE_EVENTS,
} from '../../../../shared/components/VideoPlayer/'
import { CaptionsButton } from '../../components/CaptionsButton/CaptionsButton'
import Spinner from '../../components/Spinner/Spinner'
import Platform from '../../platforms'
import { store } from '../../store'
import {
  useKeyDownHandler,
  KeyTypeEnum,
} from '../../../../shared/hooks/useKeyDownHandler'
import configuration from '../../configuration.js'
import tts from '../../lib/TTS.js'

import type { RootState, Channel } from '../../reducers/types/reducerTypes'
import type { LivePlayerScreenProps, RouterState, LivePlayer } from './types'
import ForbiddenActionMessage from '../../components/ForbiddenActionMessage'

type StreamData = { url: string; isDrm: boolean; licenseUrl: string | null }
type PlayerError = { code?: string; message?: string; data?: unknown }

const PLAYER_DOM_ID = 'player-live-ga'
declare const COMPILING_FOR_TIZEN: boolean

function getStreamDataFromChannel(channel: Channel): StreamData {
  const streamData: StreamData = { url: '', isDrm: false, licenseUrl: null }
  if (channel.drm_dash_url) {
    streamData.url = channel.drm_dash_url
    streamData.isDrm = true
    streamData.licenseUrl = channel.drm_widevine_license_url
  } else if (channel.non_drm_url) {
    streamData.url = channel.non_drm_url
  }
  return streamData
}

function LivePlayerScreen(props: LivePlayerScreenProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const ref = useRef<HTMLVideoElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const [captionsAvailable, setCaptionsAvailable] = useState(false)
  const [spinnerVisible, setSpinnerVisible] = useState(true)
  const [showControls, setShowControls] = useState(false)
  const {
    ref: containerRef,
    focusKey,
    focusSelf,
  } = useFocusable({
    isFocusBoundary: true,
    forceFocus: true,
    onArrowPress: (direction: string): boolean => {
      if (direction === 'left' || direction === 'right') {
        return false
      }
      setShowControls(true)
      return false
    },
    onEnterPress: () => {
      setShowControls(true)
    },
  })
  const { channel } = (location.state as RouterState) || {}
  const stationLogo =
    props.station.white_single_logo || props.station.white_cobranded_logo
  const showLogoOverlay =
    channel.logo_overlay && !props.fallbackStation.callsign && stationLogo
  let livePlayer: LivePlayer
  const keysToListen = [
    KeyTypeEnum.Right,
    KeyTypeEnum.Left,
    KeyTypeEnum.Rewind,
    KeyTypeEnum.FastForward,
    KeyTypeEnum.Pause,
  ]

  useKeyDownHandler([KeyTypeEnum.Back, KeyTypeEnum.Stop], () => {
    if (showControls) {
      setShowControls(false)
      focusSelf()
    } else {
      tts.pushTextToRead('Exiting livestream')
      navigate('/livestream?fromChannelPlayback=true', { replace: true })
    }
  })

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    tts.addTextToRead('Starting livestream')
    const { url, isDrm, licenseUrl } = getStreamDataFromChannel(channel)
    livePlayer = isDrm
      ? Platform.createLiveDRMPlayer(ref, {
          drmWidevideLicenseUrl: licenseUrl,
          contentSourceUrl: url,
          setCaptionsAvailable: setCaptionsAvailable,
          reportPlayerEvent: reportError,
        })
      : Platform.createLivePlayer(ref, {
          contentSourceUrl: url,
          setCaptionsAvailable: setCaptionsAvailable,
          reportPlayerEvent: reportError,
        })
    initPlayer()
  }, [])

  useLayoutEffect(() => {
    if (!spinnerVisible) {
      focusSelf()
    }
  }, [focusSelf, spinnerVisible])

  useEffect(() => {
    return () => {
      livePlayer.destroy()
      Platform.endPlayback()
    }
  }, [livePlayer])

  useEffect(() => {
    if (showControls) {
      resetTimer()
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [showControls])

  const initPlayer = useCallback(() => {
    const { feed_cid: pbsLivestreamChannelContentId } = channel
    const { full_name: liveStreamChannelName } = channel

    Platform.startPlayback({
      sendMediaEvents: true,
      extraParams: {
        ...((pbsLivestreamChannelContentId || undefined) && {
          pbs_livestream_channel_content_id: pbsLivestreamChannelContentId,
        }),
        ...((liveStreamChannelName || undefined) && {
          livestream_player_name: liveStreamChannelName,
        }),
        ...Platform.getLiveStreamAnalyticsParams(),
      },
    })

    livePlayer.init()
    const stationCallsign = props.fallbackStation
      ? props.fallbackStation.callsign || ''
      : props.station.call_sign || ''

    mux.monitor(`#${PLAYER_DOM_ID}`, {
      disableCookies: true,
      beaconCollectionDomain: 'qoe.pbs.org',
      respectDoNotTrack: true,
      data: {
        env_key: configuration?.advertising?.muxPropertyKey,
        video_stream_type: 'live',
        video_title: `Livestream ${
          channel.short_name || ''
        } ${stationCallsign}`,
        video_id: '-1',
        player_init_time: Date.now(),
        player_name: 'tizen-ga-live',
        viewer_device_name: props.device.deviceModel,
        experiment_name: 'ga-live',
        player_version: livePlayer.getVersion(),
      },
    })

    return () => {
      livePlayer.destroy()
      Platform.endPlayback()
    }
  }, [livePlayer])

  const toggleCaptions = useCallback(() => {
    store.dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: !props.areCaptionsEnabled },
    })
    tts.addTextToRead(`Captions ${props.areCaptionsEnabled ? 'Off' : 'On'}`)
  }, [props.areCaptionsEnabled])

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      setShowControls(false)
      focusSelf()
    }, 3000)
  }, [])

  const reportError = useCallback(
    (eventName: string, eventData: PlayerError = {}) => {
      if (eventName === PLAYER_REPORTABLE_EVENTS.ERROR) {
        const { code, message, data } = eventData
        const errorCode = code || PLAYER_ERROR_CODES.UNKNOWN_ERROR
        mux.emit(`#${PLAYER_DOM_ID}`, 'error', {
          player_error_code: errorCode,
          player_error_message: message || 'Live player Error',
          player_error_context: data || eventData,
        })
        navigate(`/live-playback/error?error-code=${errorCode}`)
      }
    },
    []
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <ForbiddenActionMessage keysToListen={keysToListen} />
      <div ref={containerRef} className="live-player-screen">
        <Spinner visible={spinnerVisible} />
        <Player
          ref={ref}
          onError={(e: Error) => {
            reportError(PLAYER_REPORTABLE_EVENTS.ERROR, e)
            navigate(
              `/live-playback/error?error-code=${PLAYER_ERROR_CODES.MEDIA}`
            )
          }}
          captionsAvailable={captionsAvailable}
          onTimeUpdate={() => setSpinnerVisible(false)}
          domID={PLAYER_DOM_ID}
        />
        {captionsAvailable && !COMPILING_FOR_TIZEN && showControls && (
          <CaptionsButton
            active={props.areCaptionsEnabled}
            onEnter={toggleCaptions}
            stealFocus={true}
          />
        )}
        {showLogoOverlay && !spinnerVisible && (
          <img className="station-bug" src={stationLogo} />
        )}
      </div>
    </FocusContext.Provider>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    areCaptionsEnabled: state.captions.enabled,
    device: state.device,
    station: state.station,
    fallbackStation: state.station.fallback_station,
  }
}

export default connect(mapStateToProps)(LivePlayerScreen)
